import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChartNoAxesColumn,
  Eye,
  EyeOff,
  Headset,
  Loader2,
  Package,
  Printer,
} from 'lucide-react';
import { fetchPassPolicy } from '../../security/passPolicyService';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import UserPortalContratos2Panel from './UserPortalContratos2Panel';

const PERM_GRUPO_ICONS = {
  headset: Headset,
  package: Package,
  printer: Printer,
  chartNoAxesColumn: ChartNoAxesColumn,
};

const TABS = [
  { id: 'perfil', label: 'Perfil' },
  { id: 'permissoes', label: 'Permissões' },
  { id: 'contratos', label: 'Contratos' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function phoneDigitsOnly(v) {
  return String(v || '').replace(/\D/g, '');
}

/** Máscara BR: 10 díg. (xx) xxxx-xxxx | 11 díg. (xx) xxxxx-xxxx */
function formatPhoneBrMaskFromDigits(digits) {
  const d = phoneDigitsOnly(digits).slice(0, 11);
  if (!d.length) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function onPhoneMaskedInputChange(raw, setMasked) {
  const d = phoneDigitsOnly(raw).slice(0, 11);
  setMasked(formatPhoneBrMaskFromDigits(d));
}

function optionalPhoneValid(masked) {
  const d = phoneDigitsOnly(masked);
  if (!d.length) return true;
  return d.length >= 10 && d.length <= 11;
}

function validateEmail(v) {
  const s = String(v || '').trim();
  if (!s) return false;
  return EMAIL_RE.test(s);
}

const PERMISSION_DEF = [
  {
    titulo: 'Operações',
    grupos: [
      {
        label: 'Atendimento',
        icon: 'headset',
        itens: [
          { key: 'op_atend_solicitar', label: 'Solicitar atendimento' },
          { key: 'op_atend_falar', label: 'Falar no chat' },
        ],
      },
      {
        label: 'Suprimentos',
        icon: 'package',
        itens: [{ key: 'op_sup_solicitar', label: 'Solicitar suprimento' }],
      },
      {
        label: 'Equipamentos',
        icon: 'printer',
        itens: [{ key: 'op_eq_parque_locado', label: 'Parque locado' }],
      },
      {
        label: 'Consultas',
        icon: 'chartNoAxesColumn',
        itens: [
          { key: 'op_cons_atend', label: 'Atendimento' },
          { key: 'op_cons_atend_dash', label: 'Atendimento (ver Dashboard)' },
          { key: 'op_cons_supr', label: 'Suprimento' },
          { key: 'op_cons_prod', label: 'Produção' },
        ],
      },
    ],
  },
  {
    titulo: 'Administrativo',
    grupos: [
      {
        label: null,
        itens: [
          { key: 'adm_doc_fisc', label: 'Documentos fiscais' },
          { key: 'adm_fin', label: 'Financeiro' },
          { key: 'adm_contratos', label: 'Contratos' },
        ],
      },
    ],
  },
];

/** Permissão editável mesmo para usuário Administrador. */
const PERM_KEY_ADMIN_EDITAVEL = 'op_cons_atend_dash';

/** Permissões de Operações (Atendimento, Suprimentos, Equipamentos, Consultas) — exigem ao menos um local se marcadas (usuário comum). */
const PERM_KEYS_REQUER_ENDERECO = [
  'op_atend_solicitar',
  'op_atend_falar',
  'op_sup_solicitar',
  'op_eq_parque_locado',
  'op_cons_atend',
  'op_cons_supr',
  'op_cons_prod',
];

function hasAlgumaPermissaoMarcada(permObj) {
  return Object.keys(permObj || {}).some((k) => permObj[k]);
}

function precisaEnderecoPorPermissoes(permObj, tipoUsuario) {
  if (tipoUsuario !== 'comum') return false;
  return PERM_KEYS_REQUER_ENDERECO.some((k) => permObj[k]);
}

function PermSubIcon({ icon }) {
  if (!icon) return null;
  const Cmp = PERM_GRUPO_ICONS[icon];
  if (!Cmp) return null;
  return <Cmp className="uc-upf-perm-sub-ic" size={20} strokeWidth={2} aria-hidden />;
}

function emptyPermissions() {
  const o = {};
  PERMISSION_DEF.forEach((sec) => {
    sec.grupos.forEach((g) => {
      g.itens.forEach((it) => {
        o[it.key] = false;
      });
    });
  });
  return o;
}

function buildPasswordChecklist(policy) {
  if (!policy) {
    return {
      min: 6,
      max: 32,
      items: [],
    };
  }
  const min = Math.max(4, Number(policy.minLength) || 6);
  const max = Math.min(32, Number(policy.maxLength) || 32);
  const items = [
    {
      id: 'len',
      label: `Entre ${min} e ${max} caracteres`,
      test: (s) => {
        const len = String(s || '').length;
        return len >= min && len <= max;
      },
    },
  ];
  if (policy.requireSpecialChar) {
    items.push({
      id: 'spec',
      label: 'Pelo menos um caractere especial',
      test: (s) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(s),
    });
  }
  if (policy.requireUppercase) {
    items.push({
      id: 'up',
      label: 'Pelo menos uma letra maiúscula',
      test: (s) => /[A-Z]/.test(s),
    });
  }
  if (policy.requireLowercase) {
    items.push({
      id: 'lo',
      label: 'Pelo menos uma letra minúscula',
      test: (s) => /[a-z]/.test(s),
    });
  }
  if (policy.requireNumber) {
    items.push({
      id: 'num',
      label: 'Pelo menos um número',
      test: (s) => /[0-9]/.test(s),
    });
  }
  return { min, max, items };
}

function passwordChecklistAllOk(checklist, senha) {
  const s = String(senha || '');
  if (!checklist?.items?.length) {
    const len = s.length;
    return len >= 6 && len <= 32;
  }
  return checklist.items.every((it) => it.test(s));
}

async function apiVcGet(path) {
  const token = localStorage.getItem('authToken');
  const res = await fetch(`${getApiBaseUrl()}/api/vc-usr${path}`, {
    headers: { Authorization: token ? `Bearer ${token}` : '' },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.message || `Erro ${res.status}`);
  }
  return json;
}

export default function UserPortalFormPanel({
  mode,
  user,
  idGrupoRef,
  codigosCliente,
  onClose,
  onSave,
  draft = null,
  /** Se preenchido, bloqueia Salvar e exibe tooltip branco moderno no botão. */
  blockSaveTooltip = null,
}) {
  const [tab, setTab] = useState('perfil');

  const [nome, setNome] = useState(() =>
    mode === 'edit' && user ? user.nome : String(draft?.nome || '')
  );
  const [email, setEmail] = useState(() =>
    mode === 'edit' && user ? user.email : String(draft?.email || '')
  );
  const [senha, setSenha] = useState(() => String(draft?.senha || ''));
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [passPolicy, setPassPolicy] = useState(null);
  const [cargo, setCargo] = useState(() => (mode === 'edit' && user ? user.cargo : ''));
  const [foneCelular, setFoneCelular] = useState(() =>
    mode === 'edit' ? '' : formatPhoneBrMaskFromDigits(draft?.foneWhatsapp || draft?.phone || '')
  );
  const [foneFixoMasked, setFoneFixoMasked] = useState('');
  const [usuarioInativo, setUsuarioInativo] = useState(false);
  const [tipoUsuario, setTipoUsuario] = useState(() =>
    mode === 'edit' && user ? user.tipoUsuario ?? null : null
  );

  const [permissoes, setPermissoes] = useState(() => {
    const o = emptyPermissions();
    if (mode === 'edit' && user?.tipoUsuario === 'administrador') {
      Object.keys(o).forEach((k) => {
        if (k !== PERM_KEY_ADMIN_EDITAVEL) o[k] = true;
      });
    }
    return o;
  });

  const [contratos, setContratos] = useState([]);
  const [contratosLoading, setContratosLoading] = useState(false);
  const [expandedContratoIds, setExpandedContratoIds] = useState(() => new Set());
  const [selectedLocalIds, setSelectedLocalIds] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  const [emailInvalid, setEmailInvalid] = useState(false);
  const [tipoUsuarioErro, setTipoUsuarioErro] = useState(false);
  const [perfilCampoErro, setPerfilCampoErro] = useState(null);
  const [permComumMinErro, setPermComumMinErro] = useState(false);
  const [permEnderecoErro, setPermEnderecoErro] = useState(false);
  const [saveApiError, setSaveApiError] = useState(null);
  const [saveBlockPulse, setSaveBlockPulse] = useState(false);

  const nomeRef = useRef(null);
  const emailRef = useRef(null);
  const senhaRef = useRef(null);
  const tipoFieldsetRef = useRef(null);
  const permOperacoesRef = useRef(null);

  const codigosKey = useMemo(() => (codigosCliente || []).join('|'), [codigosCliente]);

  const scrollToRef = useCallback((ref, focusRef) => {
    window.setTimeout(() => {
      ref?.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      if (focusRef?.current) focusRef.current.focus();
    }, 120);
  }, []);

  const clearValidacaoSalvar = useCallback(() => {
    setPerfilCampoErro(null);
    setPermComumMinErro(false);
    setPermEnderecoErro(false);
    setSaveApiError(null);
  }, []);

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const p = await fetchPassPolicy();
        if (!c) setPassPolicy(p);
      } catch {
        if (!c) setPassPolicy(null);
      }
    })();
    return () => {
      c = true;
    };
  }, []);

  useEffect(() => {
    if (!codigosKey) {
      setContratos([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setContratosLoading(true);
      try {
        const q = encodeURIComponent((codigosCliente || []).join(','));
        const j = await apiVcGet(`/portal-contratos-locais?codigos=${q}`);
        if (!cancelled) setContratos(Array.isArray(j.data) ? j.data : []);
      } catch {
        if (!cancelled) setContratos([]);
      } finally {
        if (!cancelled) setContratosLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [codigosKey, codigosCliente]);

  useEffect(() => {
    if (mode !== 'edit' || !user?.id || !idGrupoRef || !codigosKey) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const q = encodeURIComponent((codigosCliente || []).join(','));
        const j = await apiVcGet(
          `/portal-usuarios/${user.id}/detail?idGrupoRef=${encodeURIComponent(idGrupoRef)}&codigos=${q}`
        );
        if (cancelled || !j.data) return;
        const d = j.data;
        setNome(d.nome || '');
        setEmail(d.email || '');
        setCargo(d.cargo || '');
        setFoneCelular(formatPhoneBrMaskFromDigits(d.foneWhatsapp || ''));
        setFoneFixoMasked(formatPhoneBrMaskFromDigits(d.foneFixo || ''));
        setUsuarioInativo(d.isActive === false);
        setTipoUsuario(d.tipoUsuario || null);
        setSenha('');
        const base = emptyPermissions();
        const src = d.permissoes && typeof d.permissoes === 'object' ? d.permissoes : {};
        Object.keys(base).forEach((k) => {
          if (typeof src[k] === 'boolean') base[k] = src[k];
        });
        if (d.tipoUsuario === 'administrador') {
          Object.keys(base).forEach((k) => {
            if (k !== PERM_KEY_ADMIN_EDITAVEL) base[k] = true;
          });
        }
        setPermissoes(base);
        setSelectedLocalIds(new Set(Array.isArray(d.selectedLocalIds) ? d.selectedLocalIds : []));
      } catch {
        /* mantém estado da linha */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, user?.id, idGrupoRef, codigosKey, codigosCliente]);

  const permissoesTodasMarcadas = useCallback(() => {
    const o = emptyPermissions();
    Object.keys(o).forEach((k) => {
      o[k] = true;
    });
    return o;
  }, []);

  useEffect(() => {
    if (tipoUsuario === 'administrador') {
      setPermissoes((prev) => {
        const next = permissoesTodasMarcadas();
        next[PERM_KEY_ADMIN_EDITAVEL] = prev[PERM_KEY_ADMIN_EDITAVEL];
        return next;
      });
    }
  }, [tipoUsuario, permissoesTodasMarcadas]);

  const toggleContrato = useCallback((id) => {
    setExpandedContratoIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);

  const toggleLocal = useCallback((id) => {
    setPermEnderecoErro(false);
    setSelectedLocalIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);

  const togglePerm = useCallback((key) => {
    setPermComumMinErro(false);
    setPermEnderecoErro(false);
    setPermissoes((p) => ({ ...p, [key]: !p[key] }));
  }, []);

  const selecionarTodosDoContrato = useCallback(
    (c, marcar) => {
      setPermEnderecoErro(false);
      setSelectedLocalIds((prev) => {
        const n = new Set(prev);
        c.locais.forEach((l) => {
          if (marcar) n.add(l.id);
          else n.delete(l.id);
        });
        return n;
      });
    },
    []
  );

  const pwChecklist = useMemo(() => buildPasswordChecklist(passPolicy), [passPolicy]);

  const handleSalvar = async () => {
    if (saving) return;
    if (blockSaveTooltip) {
      setSaveBlockPulse(true);
      window.setTimeout(() => setSaveBlockPulse(false), 2200);
      return;
    }
    clearValidacaoSalvar();
    setTipoUsuarioErro(false);
    const n = nome.trim();
    const em = email.trim();
    if (!n) {
      setPerfilCampoErro('nome');
      setTab('perfil');
      scrollToRef(nomeRef, nomeRef);
      return;
    }
    if (!em) {
      setPerfilCampoErro('email');
      setTab('perfil');
      scrollToRef(emailRef, emailRef);
      return;
    }
    if (!validateEmail(em)) {
      setEmailInvalid(true);
      setPerfilCampoErro('email');
      setTab('perfil');
      scrollToRef(emailRef, emailRef);
      return;
    }
    if (!optionalPhoneValid(foneCelular)) {
      setPerfilCampoErro('foneCelular');
      setTab('perfil');
      return;
    }
    if (!optionalPhoneValid(foneFixoMasked)) {
      setPerfilCampoErro('foneFixo');
      setTab('perfil');
      return;
    }
    if (tipoUsuario !== 'comum' && tipoUsuario !== 'administrador') {
      setTipoUsuarioErro(true);
      setTab('permissoes');
      window.setTimeout(() => {
        tipoFieldsetRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        tipoFieldsetRef.current?.querySelector('input[type="radio"]')?.focus();
      }, 120);
      return;
    }
    setTipoUsuarioErro(false);
    const sTrim = senha.trim();
    if (mode !== 'edit') {
      if (!sTrim) {
        setPerfilCampoErro('senha');
        setTab('perfil');
        scrollToRef(senhaRef, senhaRef);
        return;
      }
      if (!passwordChecklistAllOk(pwChecklist, sTrim)) {
        setPerfilCampoErro('senha');
        setTab('perfil');
        scrollToRef(senhaRef, senhaRef);
        return;
      }
    } else if (sTrim && !passwordChecklistAllOk(pwChecklist, sTrim)) {
      setPerfilCampoErro('senha');
      setTab('perfil');
      scrollToRef(senhaRef, senhaRef);
      return;
    }
    if (tipoUsuario === 'comum' && !hasAlgumaPermissaoMarcada(permissoes)) {
      setPermComumMinErro(true);
      setTab('permissoes');
      window.setTimeout(() => {
        permOperacoesRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        permOperacoesRef.current?.querySelector('input[type="checkbox"]')?.focus();
      }, 120);
      return;
    }
    if (precisaEnderecoPorPermissoes(permissoes, tipoUsuario) && selectedLocalIds.size === 0) {
      setPermEnderecoErro(true);
      setTab('contratos');
      return;
    }
    const metaLoc = new Map();
    contratos.forEach((c) => {
      c.locais.forEach((l) => {
        metaLoc.set(l.id, { tb02111: l.tb02111, tb02176: l.tb02176 });
      });
    });
    const contratoLocais = [...selectedLocalIds].map((id) => metaLoc.get(id)).filter(Boolean);
    const payload = {
      mode,
      userId: mode === 'edit' && user ? user.id : null,
      nome: n,
      email: em,
      senha: sTrim,
      cargo: cargo.trim(),
      foneWhatsapp: phoneDigitsOnly(foneCelular) || undefined,
      foneFixo: phoneDigitsOnly(foneFixoMasked) || undefined,
      isActive: !usuarioInativo,
      tipoUsuario,
      permissoes: { ...permissoes },
      selectedLocalIds: [...selectedLocalIds],
      contratoLocais,
    };
    try {
      setSaving(true);
      setSaveApiError(null);
      await Promise.resolve(onSave(payload));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err || 'Erro ao salvar');
      setSaveApiError(msg);
      setTab('perfil');
      if (/já existe|e-mail neste painel|email neste painel/i.test(msg)) {
        setPerfilCampoErro('email');
        scrollToRef(emailRef, emailRef);
      } else {
        window.setTimeout(() => {
          document.querySelector('.uc-upf-api-err-banner')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }, 80);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={['uc-upf', tab === 'contratos' ? 'uc-upf--wide' : ''].filter(Boolean).join(' ')}>
      <div className="uc-upf-tabs" role="tablist" aria-label="Seções do cadastro">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={['uc-upf-tab', tab === t.id ? 'uc-upf-tab--on' : ''].join(' ')}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="uc-upf-scroll">
        {saveApiError ? (
          <p className="uc-upf-perm-err-banner uc-upf-api-err-banner" role="alert">
            {saveApiError}
          </p>
        ) : null}
        {tab === 'perfil' && (
          <div className="uc-upf-perfil">
            <div className="uc-upf-perfil-main">
              <label
                className={['uc-upf-field', perfilCampoErro === 'nome' ? 'uc-upf-field--err' : ''].filter(Boolean).join(' ')}
              >
                <span className="uc-upf-label">
                  Nome do usuário <abbr title="obrigatório">*</abbr>
                </span>
                <input
                  ref={nomeRef}
                  className={['uc-upf-input', perfilCampoErro === 'nome' ? 'uc-upf-input--invalid' : ''].filter(Boolean).join(' ')}
                  value={nome}
                  onChange={(e) => {
                    setNome(e.target.value);
                    setPerfilCampoErro(null);
                  }}
                  autoComplete="name"
                  aria-invalid={perfilCampoErro === 'nome'}
                />
                {perfilCampoErro === 'nome' ? (
                  <span className="uc-upf-field-err" role="alert">
                    Preencha o nome do usuário.
                  </span>
                ) : null}
              </label>
              <label
                className={['uc-upf-field', perfilCampoErro === 'email' || emailInvalid ? 'uc-upf-field--err' : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                <span className="uc-upf-label">
                  E-mail <abbr title="obrigatório">*</abbr>
                </span>
                <input
                  ref={emailRef}
                  className={['uc-upf-input', emailInvalid || perfilCampoErro === 'email' ? 'uc-upf-input--invalid' : ''].join(' ')}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailInvalid(false);
                    setPerfilCampoErro(null);
                    setSaveApiError(null);
                  }}
                  onBlur={() => {
                    const t = email.trim();
                    if (t) setEmailInvalid(!validateEmail(t));
                    else setEmailInvalid(false);
                  }}
                  aria-invalid={emailInvalid || perfilCampoErro === 'email'}
                />
                {perfilCampoErro === 'email' && !emailInvalid && !saveApiError ? (
                  <span className="uc-upf-field-err" role="alert">
                    Preencha o e-mail.
                  </span>
                ) : null}
                {emailInvalid ? (
                  <span className="uc-upf-field-err" role="alert">
                    E-mail inválido.
                  </span>
                ) : null}
              </label>
              <div
                className={['uc-upf-field uc-upf-field--pw', perfilCampoErro === 'senha' ? 'uc-upf-field--err' : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                <span className="uc-upf-label">
                  Senha{' '}
                  {mode === 'edit' ? null : (
                    <abbr className="uc-upf-req" title="Obrigatório">
                      *
                    </abbr>
                  )}
                </span>
                <div className="uc-upf-pw-row">
                  <input
                    ref={senhaRef}
                    className={['uc-upf-input uc-upf-input--pw', perfilCampoErro === 'senha' ? 'uc-upf-input--invalid' : '']
                      .filter(Boolean)
                      .join(' ')}
                    type={mostrarSenha ? 'text' : 'password'}
                    value={senha}
                    onChange={(e) => {
                      setSenha(e.target.value);
                      setPerfilCampoErro(null);
                    }}
                    placeholder={
                      mode === 'edit' ? 'Deixe em branco para não alterar' : 'Conforme política de senha'
                    }
                    autoComplete="new-password"
                    aria-invalid={perfilCampoErro === 'senha'}
                  />
                  <button
                    type="button"
                    className="uc-upf-pw-eye"
                    onClick={() => setMostrarSenha((v) => !v)}
                    aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {mostrarSenha ? <EyeOff size={18} strokeWidth={2} /> : <Eye size={18} strokeWidth={2} />}
                  </button>
                </div>
                {perfilCampoErro === 'senha' ? (
                  <span className="uc-upf-field-err" role="alert">
                    {mode !== 'edit' && !senha.trim()
                      ? 'Preencha a senha.'
                      : 'A senha não atende à política configurada.'}
                  </span>
                ) : null}
                {pwChecklist.items.length && senha.length > 0 ? (
                  <ul className="uc-upf-pw-rules" aria-live="polite">
                    {pwChecklist.items.map((rule) => {
                      const ok = rule.test(senha);
                      return (
                        <li
                          key={rule.id}
                          className={['uc-upf-pw-rule', ok ? 'uc-upf-pw-rule--ok' : ''].filter(Boolean).join(' ')}
                        >
                          <span className="uc-upf-pw-rule-ic" aria-hidden>
                            {ok ? '✓' : '○'}
                          </span>
                          {rule.label}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </div>
              <label className="uc-upf-field">
                <span className="uc-upf-label">Cargo</span>
                <input
                  className="uc-upf-input"
                  value={cargo}
                  onChange={(e) => setCargo(e.target.value)}
                />
              </label>
              <label
                className={['uc-upf-field', perfilCampoErro === 'foneCelular' ? 'uc-upf-field--err' : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                <span className="uc-upf-label">Celular</span>
                <input
                  className={[
                    'uc-upf-input',
                    perfilCampoErro === 'foneCelular' ? 'uc-upf-input--invalid' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="(00) 00000-0000"
                  value={foneCelular}
                  onChange={(e) => {
                    onPhoneMaskedInputChange(e.target.value, setFoneCelular);
                    setPerfilCampoErro(null);
                  }}
                  aria-invalid={perfilCampoErro === 'foneCelular'}
                />
                {perfilCampoErro === 'foneCelular' ? (
                  <span className="uc-upf-field-err" role="alert">
                    Informe 10 ou 11 dígitos ou deixe em branco.
                  </span>
                ) : null}
              </label>
              <label
                className={['uc-upf-field', perfilCampoErro === 'foneFixo' ? 'uc-upf-field--err' : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                <span className="uc-upf-label">Telefone</span>
                <input
                  className={[
                    'uc-upf-input',
                    perfilCampoErro === 'foneFixo' ? 'uc-upf-input--invalid' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  placeholder="(00) 0000-0000"
                  value={foneFixoMasked}
                  onChange={(e) => {
                    onPhoneMaskedInputChange(e.target.value, setFoneFixoMasked);
                    setPerfilCampoErro(null);
                  }}
                  aria-invalid={perfilCampoErro === 'foneFixo'}
                />
                {perfilCampoErro === 'foneFixo' ? (
                  <span className="uc-upf-field-err" role="alert">
                    Informe 10 ou 11 dígitos ou deixe em branco.
                  </span>
                ) : null}
              </label>
              <div className="uc-upf-field uc-upf-field--inativo">
                <span className="uc-upf-label">Situação</span>
                <label className="uc-upf-check">
                  <input
                    type="checkbox"
                    className="uc-upf-check-native"
                    checked={usuarioInativo}
                    onChange={(e) => setUsuarioInativo(e.target.checked)}
                  />
                  <span className="uc-upf-check-faux" aria-hidden="true" />
                  <span className="uc-upf-check-txt">Usuário inativo</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {tab === 'permissoes' && (
          <div className="uc-upf-perm">
            <fieldset
              ref={tipoFieldsetRef}
              className={['uc-upf-fieldset uc-upf-fieldset--tipo', tipoUsuarioErro ? 'uc-upf-fieldset--tipo-erro' : '']
                .filter(Boolean)
                .join(' ')}
              aria-invalid={tipoUsuarioErro || undefined}
            >
              <legend className="uc-upf-label">
                Tipo de usuário
                <abbr className="uc-upf-req" title="Obrigatório">
                  *
                </abbr>
              </legend>
              <div className="uc-upf-radio-row">
                <label className="uc-upf-radio">
                  <input
                    type="radio"
                    name="uc-tipo-usuario"
                    checked={tipoUsuario === 'comum'}
                    onChange={() => {
                      setTipoUsuarioErro(false);
                      setPermComumMinErro(false);
                      setTipoUsuario('comum');
                    }}
                  />
                  Comum
                </label>
                <label className="uc-upf-radio">
                  <input
                    type="radio"
                    name="uc-tipo-usuario"
                    checked={tipoUsuario === 'administrador'}
                    onChange={() => {
                      setTipoUsuarioErro(false);
                      setPermComumMinErro(false);
                      setPermEnderecoErro(false);
                      setTipoUsuario('administrador');
                    }}
                  />
                  Administrador
                </label>
              </div>
              {tipoUsuarioErro ? (
                <p className="uc-upf-tipo-err" role="alert">
                  Selecione Comum ou Administrador.
                </p>
              ) : null}
              {tipoUsuario === 'administrador' ? (
                <p className="uc-upf-tipo-hint">
                  Administrador: todas as permissões ficam liberadas, exceto &quot;Atendimento (ver Dashboard)&quot;, que pode ser
                  habilitada ou não.
                </p>
              ) : null}
            </fieldset>
            {permComumMinErro ? (
              <p className="uc-upf-perm-err-banner" role="alert">
                Selecione ao menos uma permissão (usuário comum).
              </p>
            ) : null}
            {PERMISSION_DEF.map((sec, secIdx) => (
              <section
                key={sec.titulo}
                ref={secIdx === 0 ? permOperacoesRef : undefined}
                className={['uc-upf-perm-sec', secIdx === 0 && permComumMinErro ? 'uc-upf-perm-sec--err' : '']
                  .filter(Boolean)
                  .join(' ')}
              >
                <h3 className="uc-upf-perm-sec-t">{sec.titulo}</h3>
                {sec.grupos.map((g, gi) => (
                  <div
                    key={g.label || `g-${sec.titulo}-${gi}`}
                    className={['uc-upf-perm-grupo', g.label ? '' : 'uc-upf-perm-grupo--flat'].join(' ')}
                  >
                    {g.label ? (
                      <div className="uc-upf-perm-sub">
                        <PermSubIcon icon={g.icon} />
                        <span>{g.label}</span>
                      </div>
                    ) : null}
                    <ul className="uc-upf-perm-list">
                      {g.itens.map((it) => (
                        <li key={it.key}>
                          <label className="uc-upf-check">
                            <input
                              type="checkbox"
                              className="uc-upf-check-native"
                              checked={!!permissoes[it.key]}
                              disabled={
                                tipoUsuario === 'administrador' && it.key !== PERM_KEY_ADMIN_EDITAVEL
                              }
                              onChange={() => togglePerm(it.key)}
                            />
                            <span className="uc-upf-check-faux" aria-hidden="true" />
                            <span className="uc-upf-check-txt">{it.label}</span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </section>
            ))}
          </div>
        )}


        {tab === 'contratos' && (
          <UserPortalContratos2Panel
            contratos={contratos}
            contratosLoading={contratosLoading}
            selectedLocalIds={selectedLocalIds}
            onToggleLocal={toggleLocal}
            onClearAll={() => {
              setPermEnderecoErro(false);
              setSelectedLocalIds(new Set());
            }}
            onSelecionarTodosDoContrato={selecionarTodosDoContrato}
            permEnderecoErro={permEnderecoErro}
            expandedContratoIds={expandedContratoIds}
            onToggleContrato={toggleContrato}
          />
        )}
      </div>

      <div className="uc-upf-foot">
        <button type="button" className="uc-upf-btn uc-upf-btn--ghost" onClick={onClose} disabled={saving}>
          Cancelar
        </button>
        <span
          className={[
            'uc-upf-save-wrap',
            blockSaveTooltip ? 'uc-upf-save-wrap--blocked cp-cad-save-tooltip-host cp-cad-save-tooltip-host--blocked' : '',
            saveBlockPulse ? 'is-pulsing' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          {...(blockSaveTooltip
            ? {
                'data-tooltip': blockSaveTooltip,
                'aria-describedby': undefined,
              }
            : {})}
        >
          <button
            type="button"
            className={['uc-upf-btn uc-upf-btn--primary', saving ? 'uc-upf-btn--saving' : '']
              .filter(Boolean)
              .join(' ')}
            onClick={handleSalvar}
            disabled={saving}
            aria-busy={saving}
            aria-disabled={Boolean(blockSaveTooltip) || undefined}
          >
            {saving ? (
              <>
                <Loader2 className="uc-upf-btn-spin" size={13} strokeWidth={2} aria-hidden />
                Processando...
              </>
            ) : (
              'Salvar'
            )}
          </button>
        </span>
      </div>
    </div>
  );
}
